import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import clsx from "clsx";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { TextInput, FieldRow } from "@/components/ui/Field";
import { Alert } from "@/components/ui/Alert";
import { formatAuftragLookupLabel } from "@/features/globalUpload/formatAuftragLookupLabel";
import { searchOrders } from "@/lib/projectOverview";
import { useProjectOverview } from "@/projectOverview/useProjectOverview";
import type { AuftragLookup } from "@/lib/material";
import {
  getAbsenceTypeLabel,
} from "@/lib/employeeAbsences";
import {
  createAssignment,
  getAssignmentFeinplanung,
  updateAssignment,
  updateAssignmentSeriesMode,
  deleteAssignment,
  DEFAULT_PLANUNG_COLOR,
  PLANUNG_COLORS,
} from "@/lib/montageplanung";
import type {
  PlanungAssignment,
  PlanungAssignmentInput,
  PlanungAbsence,
  PlanungOrderLink,
  PlanungRow,
  PlanungFeinplanungRequestSummary,
} from "@/lib/montageplanung";
import { formatDateDe } from "./dateUtils";
import { splitOrderLinkLabel } from "./displayLabels";
import {
  AssignmentMontageunterlagen,
  MontageunterlageNoteComposer,
} from "./AssignmentMontageunterlagen";
import { FinePlanningDialog } from "./FinePlanningDialog";
import { FinePlanningPreview } from "./FinePlanningPreview";
import { FinePlanningDelegatesDialog } from "./FinePlanningDelegatesDialog";
import { FeinplanungRequestApprovalCard } from "./FeinplanungRequestApprovalCard";
import { MonteurProposalApprovalCard } from "./MonteurProposalApprovalCard";
import { MP_DIALOG_Z_INDEX_CLASS } from "./layoutConstants";
import type { ConfirmDialogOptions } from "./useConfirmDialog";

function toAuftragLookup(o: {
  id: number;
  auftragsnummer?: string;
  kunde?: string;
  beschreibung?: string;
  jahr?: number;
  ort?: string;
}): AuftragLookup {
  return {
    id: o.id,
    auftragsnummer: o.auftragsnummer?.trim() || String(o.id),
    jahr: o.jahr ?? null,
    kunde: o.kunde ?? null,
    ort: o.ort ?? null,
    beschreibung: o.beschreibung ?? null,
    ort_zusatz: null,
  };
}

export function AssignmentFlagCard({
  id,
  checked,
  onChange,
  title,
  description,
  tone,
  compact = false,
}: {
  id: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  title: string;
  description: string;
  tone: "fixed" | "blocker";
  compact?: boolean;
}) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={clsx(
        "flex w-full items-center text-left transition",
        compact ? "gap-2.5 rounded-xl p-2.5" : "gap-3 rounded-2xl p-3.5",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-card",
        tone === "fixed" &&
          (checked
            ? "border-2 border-status-red/50 bg-status-red/10"
            : "border-2 border-transparent bg-fill-quaternary/50 ring-1 ring-inset ring-separator hover:bg-fill-tertiary/70"),
        tone === "blocker" &&
          (checked
            ? "border-2 border-dashed border-label/70 bg-fill-tertiary"
            : "border-2 border-transparent bg-fill-quaternary/50 ring-1 ring-inset ring-separator hover:bg-fill-tertiary/70"),
      )}
    >
      <span
        className={clsx(
          "mt-0 flex shrink-0 items-center justify-center rounded-xl",
          compact ? "h-7 w-7" : "h-9 w-9",
          tone === "fixed" &&
            (checked
              ? "bg-status-red text-base font-extrabold text-label-on-accent"
              : "bg-card text-base font-extrabold text-status-red ring-1 ring-inset ring-separator"),
          tone === "blocker" &&
            (checked
              ? "border-2 border-dashed border-label bg-card text-label"
              : "border-2 border-dashed border-label-tertiary bg-card text-label-tertiary"),
        )}
        aria-hidden
      >
        {tone === "fixed" ? "!" : null}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-label">{title}</span>
        {description ? (
          <span className="mt-0.5 block text-xs leading-snug text-label-secondary">{description}</span>
        ) : null}
      </span>
      <span
        className={clsx(
          "relative h-6 w-11 shrink-0 rounded-full transition",
          checked ? "bg-accent" : "bg-fill",
        )}
        aria-hidden
      >
        <span
          className={clsx(
            "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-all",
            checked ? "left-[1.375rem]" : "left-0.5",
          )}
        />
      </span>
    </button>
  );
}

function FormSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section className="space-y-2 rounded-2xl bg-card p-3 ring-1 ring-inset ring-separator">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-label-tertiary">
        {label}
      </p>
      {children}
    </section>
  );
}

function AssignmentKindSwitch({
  seriesMode,
  canEnableSeries,
  busy,
  onChange,
}: {
  seriesMode: boolean;
  canEnableSeries: boolean;
  busy: boolean;
  onChange: (next: boolean) => void;
}) {
  const serieDisabled = busy || (!seriesMode && !canEnableSeries);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-label-tertiary">
        Art
      </span>
      <div
        className="inline-flex rounded-lg bg-fill-quaternary p-0.5 ring-1 ring-inset ring-separator"
        role="group"
        aria-label="Einsatz oder Serie"
      >
        <button
          type="button"
          aria-pressed={!seriesMode}
          disabled={busy}
          title="Projekte gelten für alle verknüpften Termine"
          onClick={() => onChange(false)}
          className={clsx(
            "rounded-md px-2.5 py-1 text-xs font-semibold transition",
            !seriesMode
              ? "bg-card text-label shadow-sm ring-1 ring-inset ring-separator"
              : "text-label-secondary hover:text-label",
          )}
        >
          Einsatz
        </button>
        <button
          type="button"
          aria-pressed={seriesMode}
          disabled={serieDisabled}
          title={
            canEnableSeries || seriesMode
              ? "Projekte gelten nur für diesen Termin. Die Feinplanung zeigt zuerst diesen Einsatz."
              : "Zuerst mindestens einen weiteren Termin verknüpfen"
          }
          onClick={() => onChange(true)}
          className={clsx(
            "rounded-md px-2.5 py-1 text-xs font-semibold transition",
            seriesMode
              ? "bg-accent/15 text-accent shadow-sm ring-1 ring-inset ring-accent/30"
              : "text-label-secondary hover:text-label",
            serieDisabled && !seriesMode && "cursor-not-allowed opacity-45 hover:text-label-secondary",
          )}
        >
          Serie
        </button>
      </div>
    </div>
  );
}

function LinkedOrderOverviewButton({ orderId }: { orderId: number }) {
  const { open } = useProjectOverview();
  return (
    <button
      type="button"
      className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-separator bg-card px-2 text-xs font-medium text-label transition hover:bg-fill-tertiary"
      title="Projektübersicht öffnen"
      onClick={(event) => {
        event.stopPropagation();
        open(orderId);
      }}
    >
      <img
        src="/static/icons/apple-touch-icon-180.png"
        alt=""
        width={16}
        height={16}
        className="object-contain"
        decoding="async"
      />
      Übersicht
    </button>
  );
}

export interface AssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  row: PlanungRow | null;
  assignment: PlanungAssignment | null;
  defaultDate: string;
  rowAbsences: PlanungAbsence[];
  onSaved: () => void;
  onHistory?: () => void;
  onConfirm?: (options: ConfirmDialogOptions) => Promise<boolean>;
  openFeinplanungRequest?: PlanungFeinplanungRequestSummary | null;
  onApproveFeinplanungRequest?: () => void;
  onRejectFeinplanungRequest?: () => void;
  reviewingFeinplanungRequest?: boolean;
  approvingFeinplanungRequest?: boolean;
  rejectingFeinplanungRequest?: boolean;
  onApproveMonteurProposal?: () => void;
  onRejectMonteurProposal?: () => void;
  reviewingMonteurProposal?: boolean;
  approvingMonteurProposal?: boolean;
  rejectingMonteurProposal?: boolean;
}

export function AssignmentDialog({
  open,
  onClose,
  row,
  assignment,
  defaultDate,
  rowAbsences,
  onSaved,
  onHistory,
  onConfirm,
  openFeinplanungRequest,
  onApproveFeinplanungRequest,
  onRejectFeinplanungRequest,
  reviewingFeinplanungRequest,
  approvingFeinplanungRequest,
  rejectingFeinplanungRequest,
  onApproveMonteurProposal,
  onRejectMonteurProposal,
  reviewingMonteurProposal,
  approvingMonteurProposal,
  rejectingMonteurProposal,
}: AssignmentDialogProps) {
  const [title, setTitle] = useState("");
  const [color, setColor] = useState<string>(DEFAULT_PLANUNG_COLOR);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [isFixed, setIsFixed] = useState(false);
  const [isBlocker, setIsBlocker] = useState(false);
  const [orders, setOrders] = useState<PlanungOrderLink[]>([]);
  const [note, setNote] = useState("");
  const [orderQuery, setOrderQuery] = useState("");
  const [orderResults, setOrderResults] = useState<AuftragLookup[]>([]);
  const [orderSearching, setOrderSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [delegatesOpen, setDelegatesOpen] = useState(false);
  const [finePlanningOpen, setFinePlanningOpen] = useState(false);
  const [planRevision, setPlanRevision] = useState(0);
  const [seriesMode, setSeriesMode] = useState(false);
  const [seriesSaving, setSeriesSaving] = useState(false);
  const [seriesNotice, setSeriesNotice] = useState<string | null>(null);
  const searchTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSeriesNotice(null);
    setOrderQuery("");
    setOrderResults([]);
    setSeriesMode(!!assignment?.is_series);
    if (assignment) {
      setTitle(assignment.title);
      setColor(assignment.color || DEFAULT_PLANUNG_COLOR);
      setDateFrom(assignment.date_from.slice(0, 10));
      setDateTo(assignment.date_to.slice(0, 10));
      setIsFixed(assignment.is_fixed);
      setIsBlocker(assignment.is_blocker ?? false);
      setOrders(assignment.orders ?? []);
      setNote(assignment.note ?? "");
    } else {
      setTitle("");
      setColor(DEFAULT_PLANUNG_COLOR);
      setDateFrom(defaultDate);
      setDateTo(defaultDate);
      setIsFixed(false);
      setIsBlocker(false);
      setOrders([]);
      setNote("");
    }
  }, [open, assignment, defaultDate]);

  useEffect(() => {
    if (!open || !assignment) return;
    setOrders(assignment.orders ?? []);
  }, [open, assignment?.id, assignment?.orders]);

  useEffect(() => {
    if (!open) return;
    const q = orderQuery.trim();
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    if (q.length < 2) {
      setOrderResults([]);
      setOrderSearching(false);
      return;
    }
    setOrderSearching(true);
    searchTimer.current = window.setTimeout(async () => {
      try {
        const results = await searchOrders(q, 8);
        setOrderResults(results.map(toAuftragLookup));
      } catch {
        setOrderResults([]);
      } finally {
        setOrderSearching(false);
      }
    }, 250);
    return () => {
      if (searchTimer.current) window.clearTimeout(searchTimer.current);
    };
  }, [orderQuery, open]);

  const conflicts = useMemo(() => {
    if (!dateFrom || !dateTo) return [];
    return rowAbsences.filter(
      (a) => a.date_from.slice(0, 10) <= dateTo && a.date_to.slice(0, 10) >= dateFrom,
    );
  }, [rowAbsences, dateFrom, dateTo]);

  const selectedOrderIds = useMemo(() => new Set(orders.map((o) => o.order_id)), [orders]);

  const dirty = assignment
    ? title !== assignment.title ||
      color !== (assignment.color || DEFAULT_PLANUNG_COLOR) ||
      dateFrom !== assignment.date_from.slice(0, 10) ||
      dateTo !== assignment.date_to.slice(0, 10) ||
      isFixed !== assignment.is_fixed ||
      isBlocker !== (assignment.is_blocker ?? false) ||
      JSON.stringify(orders) !== JSON.stringify(assignment.orders ?? [])
    : title.trim() !== "" || orders.length > 0;

  const planDatesStale =
    !!assignment &&
    (dateFrom !== assignment.date_from.slice(0, 10) ||
      dateTo !== assignment.date_to.slice(0, 10));

  const linkedCount = assignment?.linked_assignment_ids?.length ?? 0;
  const canEnableSeries = linkedCount > 0 && !assignment?.is_company_event;

  function addOrder(o: AuftragLookup) {
    if (selectedOrderIds.has(o.id)) return;
    setOrders((prev) => [...prev, { order_id: o.id, order_label: formatAuftragLookupLabel(o) }]);
    setOrderQuery("");
    setOrderResults([]);
  }

  function removeOrder(orderId: number) {
    setOrders((prev) => prev.filter((o) => o.order_id !== orderId));
  }

  async function handleSeriesChange(next: boolean) {
    if (!assignment || next === seriesMode || seriesSaving) return;
    if (next && !canEnableSeries) return;
    const ok = onConfirm
      ? await onConfirm({
          title: next ? "Als Serie führen" : "Serie aufheben",
          confirmLabel: next ? "Als Serie führen" : "Serie aufheben",
          variant: "warning",
          message: next
            ? "Die Einsätze bleiben verbunden. Die Feinplanung zeigt zuerst diesen Einsatz, die anderen Termine der Serie können Sie dort öffnen. Projekte gelten nur für den jeweiligen Einsatz und stapeln sich nicht auf vergangenen oder späteren Terminen."
            : "Die Einsätze bleiben verknüpft. Beim nächsten Speichern gelten Projekte und Feinplanung wieder für alle Termine und können dabei überschrieben werden.",
        })
      : window.confirm(
          next
            ? "Als Serie führen? Projekte gelten dann nur für den jeweiligen Einsatz."
            : "Serie aufheben? Beim nächsten Speichern gelten Projekte wieder für alle Termine.",
        );
    if (!ok) return;
    setSeriesSaving(true);
    setError(null);
    setSeriesNotice(null);
    try {
      const result = await updateAssignmentSeriesMode(assignment.id, next);
      setSeriesMode(result.series_mode);
      const ordersUnchanged =
        orders.length === (assignment.orders ?? []).length &&
        orders.every((item, index) => item.order_id === assignment.orders?.[index]?.order_id);
      if (result.orders_localized && ordersUnchanged) {
        const plan = await getAssignmentFeinplanung(assignment.id);
        setOrders(plan.focused_assignment_orders ?? []);
      }
      setSeriesNotice(
        result.series_mode
          ? result.orders_localized
            ? "Serie ist aktiv. Projekte stehen nur noch am Termin, an dem sie in der Feinplanung liegen."
            : "Serie ist aktiv. Neue Projekte gelten nur für den jeweiligen Einsatz. Bereits übernommene Projekte ohne Tagesplanung bleiben vorerst auf jedem Termin, bis Sie sie dort entfernen."
          : "Serie ist aufgehoben. Die Einsätze bleiben verknüpft.",
      );
      setPlanRevision((revision) => revision + 1);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Serie konnte nicht geändert werden.");
    } finally {
      setSeriesSaving(false);
    }
  }

  async function handleSave() {
    if (!row) return;
    if (!title.trim()) {
      setError("Bitte einen Titel eingeben.");
      return;
    }
    if (dateTo < dateFrom) {
      setError("Das Enddatum darf nicht vor dem Startdatum liegen.");
      return;
    }
    const startDateChanged =
      assignment != null && dateFrom !== assignment.date_from.slice(0, 10);
    if (assignment?.is_fixed && startDateChanged) {
      const ok = onConfirm
        ? await onConfirm({
            title: "Fixer Termin",
            message: (
              <>
                <strong>{assignment.title}</strong> ist als fix markiert.
                <span className="mt-2 block text-label">
                  Das Startdatum würde sich ändern. Möchten Sie das trotzdem speichern?
                </span>
              </>
            ),
            confirmLabel: "Startdatum ändern",
            variant: "warning",
          })
        : window.confirm(
            `"${assignment.title}" ist als fix markiert. Startdatum trotzdem ändern?`,
          );
      if (!ok) return;
    }
    setSaving(true);
    setError(null);
    const payload: PlanungAssignmentInput = {
      row_id: row.id,
      title: title.trim(),
      color,
      date_from: dateFrom,
      date_to: dateTo,
      is_fixed: isFixed,
      is_blocker: isBlocker,
      is_company_event: assignment?.is_company_event ?? false,
      orders,
      note: note.trim() || null,
    };
    try {
      if (assignment) {
        await updateAssignment(assignment.id, payload);
      } else {
        await createAssignment(payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!assignment) return;
    const ok = onConfirm
      ? await onConfirm({
          title: "Einsatz löschen",
          message:
            "Möchten Sie diesen Einsatz wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.",
          confirmLabel: "Löschen",
          variant: "danger",
        })
      : window.confirm("Diesen Einsatz wirklich löschen?");
    if (!ok) return;
    setSaving(true);
    setError(null);
    try {
      await deleteAssignment(assignment.id);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  const rowName = row?.label || row?.employee_login || "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        assignment ? (seriesMode ? "Serie bearbeiten" : "Einsatz bearbeiten") : "Einsatz anlegen"
      }
      description={
        rowName ? (
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <span>Zeile</span>
            <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-xs font-medium text-label">
              {rowName}
            </span>
          </span>
        ) : undefined
      }
      size={assignment ? "4xl" : "lg"}
      panelClassName={assignment ? "lg:h-[min(92vh,920px)]" : undefined}
      bodyClassName={
        assignment ? "lg:flex lg:min-h-0 lg:flex-1 lg:flex-col lg:overflow-hidden" : undefined
      }
      fullScreenMobile
      zIndexClass={MP_DIALOG_Z_INDEX_CLASS}
      dirty={dirty}
      footer={
        <>
          {assignment &&
          (assignment.monteur_proposal_status !== "pending" || onHistory) ? (
            <div className="mr-auto flex flex-wrap gap-2">
              {assignment.monteur_proposal_status !== "pending" ? (
                <Button variant="danger" onClick={handleDelete} disabled={saving}>
                  Löschen
                </Button>
              ) : null}
              {onHistory ? (
                <Button variant="secondary" onClick={onHistory} disabled={saving}>
                  Verlauf
                </Button>
              ) : null}
            </div>
          ) : null}
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleSave} loading={saving}>
            Speichern
          </Button>
        </>
      }
    >
      <div
        className={
          assignment
            ? "flex min-h-0 flex-1 flex-col gap-6 lg:grid lg:grid-cols-2 lg:gap-0"
            : "space-y-4"
        }
      >
      {error && (
        <Alert variant="error" className={assignment ? "mb-4 lg:col-span-2" : undefined}>
          {error}
        </Alert>
      )}
      <div className={assignment ? "min-h-0 space-y-3 lg:overflow-y-auto lg:pr-5" : "contents"}>
        {assignment && !assignment.is_company_event ? (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <AssignmentKindSwitch
              seriesMode={seriesMode}
              canEnableSeries={canEnableSeries}
              busy={seriesSaving || saving}
              onChange={(next) => void handleSeriesChange(next)}
            />
            {seriesNotice ? (
              <p className="text-xs text-label-secondary">{seriesNotice}</p>
            ) : null}
          </div>
        ) : null}

        {assignment?.is_company_event && (
          <Alert variant="info">
            Betriebsveranstaltungen werden im Betriebsveranstaltungs-Dialog bearbeitet.
          </Alert>
        )}

        {conflicts.length > 0 && (
          <Alert variant="warning">
            Achtung: In diesem Zeitraum ist eine Abwesenheit hinterlegt (
            {conflicts
              .map((c) => `${getAbsenceTypeLabel(c.absence_type)} ${formatDateDe(c.date_from)}–${formatDateDe(c.date_to)}`)
              .join(", ")}
            ). Der Einsatz kann trotzdem gespeichert werden.
          </Alert>
        )}

        {openFeinplanungRequest &&
        onApproveFeinplanungRequest &&
        onRejectFeinplanungRequest ? (
          <FeinplanungRequestApprovalCard
            request={openFeinplanungRequest}
            onApprove={onApproveFeinplanungRequest}
            onReject={onRejectFeinplanungRequest}
            busy={reviewingFeinplanungRequest}
            approving={approvingFeinplanungRequest}
            rejecting={rejectingFeinplanungRequest}
          />
        ) : null}

        {assignment?.monteur_proposal_status === "pending" &&
        onApproveMonteurProposal &&
        onRejectMonteurProposal ? (
          <MonteurProposalApprovalCard
            assignment={assignment}
            onApprove={onApproveMonteurProposal}
            onReject={onRejectMonteurProposal}
            busy={reviewingMonteurProposal}
            approving={approvingMonteurProposal}
            rejecting={rejectingMonteurProposal}
          />
        ) : null}

        <FormSection label="Termin">
        <TextInput
          label="Titel"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="z. B. DFS Wartung Center"
          autoFocus
        />

        <div className="grid grid-cols-2 gap-3">
          <TextInput
            label="Von"
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              if (dateTo < e.target.value) setDateTo(e.target.value);
            }}
          />
          <TextInput
            label="Bis"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>

        <FieldRow
          label={
            <span className="flex w-full items-baseline justify-between gap-3">
              <span>Farbe</span>
              <span className="text-xs font-normal text-label-secondary">
                {PLANUNG_COLORS.find((c) => c.value === color)?.label}
              </span>
            </span>
          }
        >
          <div className="flex flex-wrap items-center gap-2">
            {PLANUNG_COLORS.map((c) => {
              const selected = color === c.value;
              return (
                <button
                  key={c.value}
                  type="button"
                  title={c.label}
                  aria-label={c.label}
                  aria-pressed={selected}
                  onClick={() => setColor(c.value)}
                  className={clsx(
                    "h-8 w-8 rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-card",
                    selected
                      ? "ring-2 ring-label ring-offset-2 ring-offset-card"
                      : "ring-1 ring-black/10 hover:scale-105 dark:ring-white/20",
                  )}
                  style={{ backgroundColor: c.value }}
                />
              );
            })}
          </div>
        </FieldRow>
          <div className="grid grid-cols-2 gap-2">
            <AssignmentFlagCard
              id="mp-fixed"
              tone="fixed"
              compact
              title="Fix"
              description=""
              checked={isFixed}
              onChange={setIsFixed}
            />
            <AssignmentFlagCard
              id="mp-blocker"
              tone="blocker"
              compact
              title="Blocker"
              description=""
              checked={isBlocker}
              onChange={setIsBlocker}
            />
          </div>
        </FormSection>

        <FormSection label="Aufträge">
          {seriesMode ? (
            <p className="text-xs text-label-secondary">
              Diese Aufträge gelten nur für diesen Einsatz.
            </p>
          ) : null}
          {orders.length > 0 && (
            <ul className="mb-2 space-y-2">
              {orders.map((o) => {
                const { number, details } = splitOrderLinkLabel(o.order_id, o.order_label);
                return (
                  <li
                    key={o.order_id}
                    className="flex flex-wrap items-center gap-2 rounded-xl bg-fill-quaternary/70 px-3 py-2.5 ring-1 ring-inset ring-separator"
                  >
                    <div className="min-w-[10rem] flex-1">
                      <div className="truncate font-mono text-[13px] font-semibold tracking-tight text-label">
                        {number}
                      </div>
                      {details ? (
                        <div className="mt-0.5 line-clamp-2 text-xs leading-snug text-label-secondary">
                          {details}
                        </div>
                      ) : null}
                    </div>
                    <LinkedOrderOverviewButton orderId={o.order_id} />
                    <button
                      type="button"
                      className="inline-flex h-8 shrink-0 items-center rounded-lg px-2 text-xs font-medium text-label-secondary transition hover:bg-status-red/10 hover:text-status-red"
                      onClick={() => removeOrder(o.order_id)}
                    >
                      Entfernen
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="relative">
            <MagnifyingGlassIcon
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-label-tertiary"
              aria-hidden
            />
            <input
              className="eh-input !pl-9"
              value={orderQuery}
              onChange={(e) => setOrderQuery(e.target.value)}
              placeholder="Auftrag suchen und hinzufügen…"
              aria-label="Auftrag suchen und hinzufügen"
            />
            {(orderSearching || orderResults.length > 0) && orderQuery.trim().length >= 2 && (
              <div className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-xl border border-separator bg-card p-1 shadow-ios-md">
                {orderSearching && (
                  <div className="px-3 py-2 text-sm text-label-secondary">Suche…</div>
                )}
                {!orderSearching &&
                  orderResults
                    .filter((o) => !selectedOrderIds.has(o.id))
                    .map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        className="block w-full rounded-lg px-3 py-2 text-left text-sm line-clamp-2 break-words hover:bg-fill-tertiary"
                        onClick={() => addOrder(o)}
                      >
                        {formatAuftragLookupLabel(o)}
                      </button>
                    ))}
                {!orderSearching &&
                  orderResults.filter((o) => !selectedOrderIds.has(o.id)).length === 0 && (
                    <div className="px-3 py-2 text-sm text-label-secondary">Keine Treffer</div>
                  )}
              </div>
            )}
          </div>
        </FormSection>

        {!assignment?.is_company_event ? (
          <>
            <FormSection label="Hinweis für diesen Einsatz">
              <textarea
                className="eh-input min-h-[4.5rem]"
                rows={2}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="z. B. Morgens in der Werkstatt treffen"
                aria-label="Hinweis für diesen Einsatz"
              />
              <p className="text-xs leading-relaxed text-label-secondary">
                Gilt nur für diesen Einsatz und erscheint im Hover. Nicht für das gesamte Projekt
                und nicht in den Montageunterlagen.
              </p>
            </FormSection>
            <FormSection label="Montageunterlagennotiz">
              <MontageunterlageNoteComposer orders={orders} />
            </FormSection>
          </>
        ) : assignment?.note?.trim() ? (
          <FormSection label="Bisherige Aufgabe">
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-label">
              {assignment.note.trim()}
            </p>
          </FormSection>
        ) : null}
      </div>

      {assignment && (
        <div className="flex min-h-[420px] min-w-0 flex-col gap-3 rounded-2xl bg-fill-quaternary/40 p-4 ring-1 ring-inset ring-separator lg:min-h-0 lg:overflow-hidden lg:rounded-none lg:border-l lg:border-separator lg:bg-transparent lg:py-0 lg:pl-5 lg:pr-0 lg:ring-0">
          <div className="flex min-h-[10rem] min-w-0 flex-1 flex-col">
            <FinePlanningPreview
              assignmentId={assignment.id}
              active={open}
              refreshKey={planRevision}
              datesStale={planDatesStale}
              editorCount={assignment.feinplanung_editors?.length ?? 0}
              focusDateFrom={assignment.date_from}
              focusDateTo={assignment.date_to}
              focusTitle={assignment.title}
              onEdit={() => setFinePlanningOpen(true)}
              onRelease={() => setDelegatesOpen(true)}
            />
          </div>
          <AssignmentMontageunterlagen orders={orders} active={open} />
        </div>
      )}
      </div>

      {assignment && (
        <FinePlanningDialog
          open={finePlanningOpen}
          onClose={() => setFinePlanningOpen(false)}
          assignmentId={assignment.id}
          assignmentTitle={title.trim() || assignment.title}
          savedDateFrom={assignment.date_from}
          savedDateTo={assignment.date_to}
          onSaved={(assignmentOrders) => {
            if (seriesMode) setOrders(assignmentOrders ?? []);
            else if (assignmentOrders?.length) setOrders(assignmentOrders);
            setPlanRevision((revision) => revision + 1);
            onSaved();
          }}
        />
      )}

      {assignment && (
        <FinePlanningDelegatesDialog
          open={delegatesOpen}
          onClose={() => setDelegatesOpen(false)}
          assignmentId={assignment.id}
          assignmentTitle={title.trim() || assignment.title}
          initialEditors={assignment.feinplanung_editors ?? []}
          onChanged={onSaved}
        />
      )}
    </Dialog>
  );
}
